import React, { useState, useEffect } from 'react';
import { Input, Checkbox, Button, Select } from 'antd';
import { SearchOutlined, DownOutlined, TeamOutlined } from '@ant-design/icons';
import { useGetRolesByLevelQuery } from '../../services/role';
import { useGetStaffListQuery } from '../../services/user';
import './styles.scss';

const TeamMemberSelect = ({ open, onClose, onSelect, selectedMembers = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState(selectedMembers);
  const [roleFilter, setRoleFilter] = useState([]);

  // Keep the internal checkbox state in sync with the parent (e.g. after "Clear").
  useEffect(() => {
    setSelected(selectedMembers);
  }, [open, selectedMembers]);
  // Which control is expanded in the filter row: 'search' | 'role' (one at a time).
  const [mode, setMode] = useState('search');

  // Permission-free public roles endpoint (/roles/public).
  const { data: rolesData } = useGetRolesByLevelQuery();
  const roleOptions = (rolesData?.data || []).map(r => ({ value: r._id, label: r.name }));

  // Staff list from /users/staff-list?roleId= (filtered by the selected role[s]).
  const { data: staffData, isFetching: staffLoading } = useGetStaffListQuery(
    { roleId: roleFilter.length ? roleFilter.join(',') : undefined },
    { skip: !open },
  );

  const teamMembers = ((staffData?.data ?? staffData ?? []) as any[]).map((u) => ({
    id:     u._id ?? u.id,
    name:   u.name ?? u.fullName ?? '—',
    email:  u.email ?? u.phoneNumber ?? '',
    avatar: u.photo ?? u.avatar ?? null,
  }));

  const filteredMembers = teamMembers.filter(member => {
    const q = searchTerm.toLowerCase();
    return (
      member.name.toLowerCase().includes(q) ||
      (member.email || '').toLowerCase().includes(q)
    );
  });

  const handleToggle = (memberId) => {
    setSelected(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleDone = () => {
    if (onSelect) {
      onSelect(selected);
    }
    if (onClose) {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="team-member-select-overlay" onClick={onClose}>
      <div className="team-member-select" onClick={(e) => e.stopPropagation()}>
        <div className="search-section">
          {mode === 'search' ? (
            <Input
              autoFocus
              allowClear
              placeholder="Search team members"
              prefix={<SearchOutlined style={{ color: '#999' }} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          ) : (
            <button
              type="button"
              className="tms-toggle tms-search-toggle"
              onClick={() => setMode('search')}
              title="Search"
            >
              <SearchOutlined />
            </button>
          )}

          {mode === 'role' ? (
            <Select
              className="role-filter"
              popupClassName="role-filter-dropdown"
              mode="multiple"
              placeholder="Filter by role"
              allowClear
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
              value={roleFilter}
              onChange={(vals) => setRoleFilter(vals || [])}
              style={{ width: '100%' }}
            >
              {roleOptions.map(o => (
                <Select.Option key={o.value} value={o.value} label={o.label}>
                  {o.label}
                </Select.Option>
              ))}
            </Select>
          ) : (
            <button
              type="button"
              className="tms-toggle tms-role-toggle"
              onClick={() => setMode('role')}
              title="Filter by role"
            >
              <TeamOutlined />
              <span className="tms-role-text">
                {roleFilter.length ? `${roleFilter.length} role${roleFilter.length > 1 ? 's' : ''}` : 'Role'}
              </span>
              <DownOutlined className="tms-role-caret" />
            </button>
          )}
        </div>

        <div className="members-list">
          {staffLoading && <div className="tms-list-state">Loading…</div>}
          {!staffLoading && filteredMembers.length === 0 && (
            <div className="tms-list-state">No team members found</div>
          )}
          {!staffLoading && filteredMembers.map(member => (
            <div 
              key={member.id} 
              className="member-item"
              onClick={() => handleToggle(member.id)}
            >
              <div className="member-info">
                <div className="member-avatar">
                  {member.avatar ? (
                    <img src={member.avatar} alt={member.name} />
                  ) : (
                    <div className="avatar-placeholder">
                      {member.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="member-details">
                  <div className="member-name">{member.name}</div>
                  {member.email && <div className="member-email">{member.email}</div>}
                </div>
              </div>
              <Checkbox 
                checked={selected.includes(member.id)}
                onClick={(e) => e.stopPropagation()}
                onChange={() => handleToggle(member.id)}
              />
            </div>
          ))}
        </div>

        <div className="done-section">
          <Button type="primary" block size="large" onClick={handleDone}>
            DONE
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TeamMemberSelect;
